import { useCallback } from "react";
import { useNavigate, type NavigateFunction } from "react-router-dom";
import { ROUTES } from "@/shared/config/routes";
import { teardownSession } from "@/features/auth/lib/sessionTeardown";

/**
 * replace 네비게이션 안전망(#405). WebKit 은 push+replace 합산 100회/10초를 넘기면 SecurityError 를 던지고
 * react-router 의 replace 는 이를 잡지 않는다. 앱 상태(토큰·스토어)는 이미 갱신된 뒤이므로 하드 이동으로
 * 마무리해 "로그인 실패" 오보를 막는다. 근본 수정(세션 무효 1회 가드)이 아닌 최후 장치다 — 발동 자체가 이상 신호.
 */
const replaceOrReload = (navigate: NavigateFunction, path: string): void => {
  try {
    navigate(path, { replace: true });
  } catch (error) {
    if (error instanceof DOMException && error.name === "SecurityError") {
      window.location.replace(path);
      return;
    }
    throw error;
  }
};

export function useAuthNavigation() {
  const navigate = useNavigate();

  // useCallback 은 렌더마다 새 클로저가 만들어져 구독자(useAuthSessionRecovery)가 매번 재구독하던 것을 줄이는
  // 보조다. BrowserRouter 의 navigate 는 location 이 바뀌면 identity 가 바뀌므로 재구독 가능성은 남는다.
  const goAfterLogin = useCallback(() => replaceOrReload(navigate, ROUTES.MAIN), [navigate]);

  // logOut 은 모든 세션무효(자동 401·계정상태·명시 로그아웃)가 수렴하는 chokepoint 다
  // (useAuthSessionRecovery 가 세션무효 이벤트마다 logOut 호출). 정리 순서·범위는 teardownSession 이 소유한다(#394).
  const logOut = useCallback(() => {
    teardownSession();
    replaceOrReload(navigate, ROUTES.LOGIN);
  }, [navigate]);

  return { goAfterLogin, logOut };
}
